import db from "../db/database";
import { getCurrentDateTimeISO } from "../utils/dateUtils";
import { getAperturaActiva, clearAperturaActiva } from "./cajaEstadoService";

// Función para obtener el siguiente número de cierre de caja
export const getNextCierreDeCajaNumber = async () => {
    try {
        // Obtener todos los cierres para encontrar el número más alto
        const allCierres = await db.cierreDeCaja.toArray();

        if (allCierres.length === 0) {
            // Si no hay cierres, empezar desde C00001
            return "C00001";
        }

        // Filtrar cierres con formato válido (que empiecen con C seguido de números)
        const validNumbers = allCierres
            .map(cierre => cierre.No)
            .filter(no => no && /^C\d+$/.test(no))
            .map(no => parseInt(no.substring(1), 10));

        if (validNumbers.length === 0) {
            return "C00001";
        }

        // Encontrar el número máximo y agregar 1
        const maxNumber = Math.max(...validNumbers);
        const nextNumber = maxNumber + 1;

        // Formatear el número con ceros a la izquierda (C00001, C00002, etc.)
        return `C${nextNumber.toString().padStart(5, '0')}`;
    } catch (error) {
        console.error("Error al generar el siguiente número de cierre:", error);
        // En caso de error, generar un número basado en la fecha y hora actual
        const timestamp = Date.now().toString().slice(-6);
        return `C${timestamp}`;
    }
};

// Funciones para gestionar cierres de caja

export const addCierreDeCaja = async (cierre) => {
    try {
        // Verificar que exista una apertura de caja activa
        const aperturaActiva = await getAperturaActiva();
        if (!aperturaActiva) {
            throw new Error("No hay una caja abierta actualmente. Debe abrir una caja antes de realizar un cierre.");
        }

        // Si el idApertura no está definido, usar el de la apertura activa
        if (!cierre.idApertura) {
            cierre.idApertura = aperturaActiva.No;
        }

        // Agregar campos de control
        const now = getCurrentDateTimeISO();
        const cierreWithMeta = {
            ...cierre,
            pendingSync: 1,
            lastModified: now,
            lastSynced: null,
            deleted: false,
        };

        // Guardar cierre en la base de datos
        const id = await db.cierreDeCaja.add(cierreWithMeta);
        const nuevoCierre = { ...cierreWithMeta, id };

        // Si el estado del cierre es "Cerrado", actualizar la apertura relacionada y limpiar apertura activa
        if (cierre.Estado === "Cerrado") {
            try {
                // Actualizar estado de la apertura a "Cerrado"
                await db.aperturaDeCaja.update(aperturaActiva.id, {
                    Estado: "Cerrado",
                    lastModified: now,
                    pendingSync: 1
                });

                // Limpiar la caja activa
                await clearAperturaActiva();
            } catch (error) {
                console.error("Error al actualizar estado de apertura:", error);
                // No bloqueamos el flujo principal por este error
            }
        }

        // Devolver el cierre con su ID asignado
        return nuevoCierre;
    } catch (error) {
        console.error("Error al agregar cierre de caja:", error);
        throw error;
    }
};

export const getAllCierresDeCaja = async () => {
    try {
        return await db.cierreDeCaja
            .filter((cierre) => !cierre.deleted)
            .toArray();
    } catch (error) {
        console.error("Error al obtener cierres de caja:", error);
        throw error;
    }
};

export const getCierreDeCajaById = async (id) => {
    try {
        // Convertir ID a número si es necesario
        const numericId = parseInt(id, 10);
        if (isNaN(numericId)) {
            throw new Error("ID de cierre de caja no es un número válido");
        }

        return await db.cierreDeCaja.get(numericId);
    } catch (error) {
        console.error(`Error al obtener cierre de caja con ID ${id}:`, error);
        throw error;
    }
};

export const getCierreDeCajaByNo = async (No) => {
    try {
        return await db.cierreDeCaja
            .where("No")
            .equals(No)
            .and((cierre) => !cierre.deleted)
            .first();
    } catch (error) {
        console.error(`Error al obtener cierre de caja con No ${No}:`, error);
        throw error;
    }
};

export const updateCierreDeCaja = async (id, updatedCierre) => {
    try {
        // Verificar primero si el cierre existe y no está sincronizado
        const existingCierre = await getCierreDeCajaById(id);
        if (!existingCierre) {
            throw new Error("El cierre de caja no existe");
        }

        // No permitir actualizar si ya tiene número de BC
        if (existingCierre.No) {
            throw new Error("No se puede modificar un cierre de caja que ya ha sido sincronizado con Business Central");
        }

        const now = getCurrentDateTimeISO();

        // Actualizar campos de control
        const cierreToUpdate = {
            ...updatedCierre,
            pendingSync: 1,
            lastModified: now,
        };

        // Guardar el estado anterior para comparar
        const estadoAnterior = existingCierre.Estado;

        // Actualizar cierre en la base de datos
        await db.cierreDeCaja.update(id, cierreToUpdate);

        const cierreActualizado = { ...cierreToUpdate, id };

        // Si el estado cambió a "Cerrado", actualizar la apertura relacionada y limpiar apertura activa
        if (estadoAnterior !== "Cerrado" && cierreActualizado.Estado === "Cerrado") {
            try {
                const aperturaActiva = await getAperturaActiva();
                if (aperturaActiva && aperturaActiva.No === cierreActualizado.idApertura) {
                    // Actualizar estado de la apertura a "Cerrado"
                    await db.aperturaDeCaja.update(aperturaActiva.id, {
                        Estado: "Cerrado",
                        lastModified: now,
                        pendingSync: 1
                    });

                    // Limpiar la caja activa
                    await clearAperturaActiva();
                }
            } catch (error) {
                console.error("Error al actualizar estado de apertura:", error);
                // No bloqueamos el flujo principal por este error
            }
        }

        return cierreActualizado;
    } catch (error) {
        console.error(`Error al actualizar cierre de caja con ID ${id}:`, error);
        throw error;
    }
};

export const deleteCierreDeCaja = async (id) => {
    try {
        // Verificar que el ID sea válido
        if (!id && id !== 0) {
            throw new Error("ID de cierre de caja inválido");
        }

        // Convertir ID a número si es necesario
        const numericId = parseInt(id, 10);
        if (isNaN(numericId)) {
            throw new Error("ID de cierre de caja no es un número válido");
        }

        // Verificar primero si el cierre existe y no está sincronizado
        const existingCierre = await getCierreDeCajaById(numericId);

        if (!existingCierre) {
            throw new Error("El cierre de caja no existe");
        }

        // No permitir eliminar si ya tiene número de BC
        if (existingCierre.No) {
            throw new Error("No se puede eliminar un cierre de caja que ya ha sido sincronizado con Business Central");
        }

        const now = getCurrentDateTimeISO();

        // Marcar como eliminado (soft delete)
        const updateCount = await db.cierreDeCaja.update(numericId, {
            deleted: true,
            pendingSync: 1,
            lastModified: now,
        });

        if (updateCount === 0) {
            throw new Error("No se pudo marcar el cierre de caja como eliminado");
        }

        // También marcar las líneas asociadas como eliminadas
        const cierreDetalles = await db.cierreDeCajaDetalle
            .where("cierreId")
            .equals(numericId)
            .toArray();

        for (const detalle of cierreDetalles) {
            await db.cierreDeCajaDetalle.update(detalle.id, {
                deleted: true,
                pendingSync: 1,
                lastModified: now,
            });
        }

        return true;
    } catch (error) {
        console.error(`Error al eliminar cierre de caja con ID ${id}:`, error);
        throw error;
    }
};

// Funciones adicionales para trabajar con las líneas de detalle del cierre

export const addCierreDetalle = async (cierreDetalle) => {
    try {
        // Verificar primero si el cierre existe y no está sincronizado
        const parentCierre = await getCierreDeCajaById(cierreDetalle.cierreId);
        if (!parentCierre) {
            throw new Error("El cierre de caja no existe");
        }

        // No permitir añadir líneas si el cierre ya está sincronizado
        if (parentCierre.No) {
            throw new Error("No se pueden añadir detalles a un cierre de caja que ya ha sido sincronizado con Business Central");
        }

        const now = getCurrentDateTimeISO();
        const detalleWithMeta = {
            ...cierreDetalle,
            pendingSync: 1,
            lastModified: now,
            lastSynced: null,
            deleted: false,
        };

        const id = await db.cierreDeCajaDetalle.add(detalleWithMeta);
        return { ...detalleWithMeta, id };
    } catch (error) {
        console.error("Error al agregar detalle de cierre de caja:", error);
        throw error;
    }
};

export const getCierreDetalles = async (cierreId) => {
    try {
        return await db.cierreDeCajaDetalle
            .where("cierreId")
            .equals(cierreId)
            .and((detalle) => !detalle.deleted)
            .toArray();
    } catch (error) {
        console.error(`Error al obtener detalles de cierre para ID ${cierreId}:`, error);
        throw error;
    }
};

export const updateCierreDetalle = async (id, updatedDetalle) => {
    try {
        // Obtener la línea actual
        const existingDetalle = await db.cierreDeCajaDetalle.get(id);
        if (!existingDetalle) {
            throw new Error("El detalle de cierre no existe");
        }

        // Verificar si el cierre padre está sincronizado
        const parentCierre = await getCierreDeCajaById(existingDetalle.cierreId);
        if (!parentCierre) {
            throw new Error("El cierre de caja no existe");
        }

        // No permitir actualizar si el cierre ya está sincronizado
        if (parentCierre.No) {
            throw new Error("No se pueden modificar detalles de un cierre que ya ha sido sincronizado con Business Central");
        }

        const now = getCurrentDateTimeISO();

        const detalleToUpdate = {
            ...updatedDetalle,
            pendingSync: 1,
            lastModified: now,
        };

        await db.cierreDeCajaDetalle.update(id, detalleToUpdate);
        return { ...detalleToUpdate, id };
    } catch (error) {
        console.error(`Error al actualizar detalle de cierre con ID ${id}:`, error);
        throw error;
    }
};

export const deleteCierreDetalle = async (id) => {
    try {
        // Obtener la línea actual
        const existingDetalle = await db.cierreDeCajaDetalle.get(id);
        if (!existingDetalle) {
            throw new Error("El detalle de cierre no existe");
        }

        // Verificar si el cierre padre está sincronizado
        const parentCierre = await getCierreDeCajaById(existingDetalle.cierreId);
        if (!parentCierre) {
            throw new Error("El cierre de caja no existe");
        }

        // No permitir eliminar si el cierre ya está sincronizado
        if (parentCierre.No) {
            throw new Error("No se pueden eliminar detalles de un cierre que ya ha sido sincronizado con Business Central");
        }

        const now = getCurrentDateTimeISO();

        await db.cierreDeCajaDetalle.update(id, {
            deleted: true,
            pendingSync: 1,
            lastModified: now,
        });

        return true;
    } catch (error) {
        console.error(`Error al eliminar detalle de cierre con ID ${id}:`, error);
        throw error;
    }
};

// Función para obtener cierre completo con sus detalles
export const getCompleteCierreDeCaja = async (cierreId) => {
    try {
        const cierre = await getCierreDeCajaById(cierreId);
        if (!cierre) return null;

        const detalles = await getCierreDetalles(cierreId);
        return { ...cierre, detalles };
    } catch (error) {
        console.error(`Error al obtener cierre completo con ID ${cierreId}:`, error);
        throw error;
    }
};